import api from './api'

export type UserRole = 'master' | 'admin' | 'user'

export interface CompanyUser {
  membership_id: string
  user_id: string
  name: string
  email: string
  phone?: string
  avatar_url?: string
  department?: string
  cargo?: string
  role: UserRole
  is_active: boolean
  joined_at: string
}

export interface CompanyInvite {
  id: string
  email: string
  token: string
  role: UserRole
  expires_at: string
  accepted_at?: string
  created_at: string
  invited_by_name?: string
}

/**
 * List all users in the current company
 */
export async function listCompanyUsers(): Promise<{ users: CompanyUser[]; total: number }> {
  const response = await api.get('/company-users')
  return response.data
}

/**
 * Get current user's role in the company
 */
export async function getCurrentUserRole(): Promise<{ role: UserRole }> {
  const response = await api.get('/company-users/my-role')
  return response.data
}

/**
 * Update user role in the company
 */
export async function updateUserRole(userId: string, role: UserRole): Promise<{ success: boolean; message: string }> {
  const response = await api.put(`/company-users/${userId}/role`, { role })
  return response.data
}

/**
 * Update user status (activate/deactivate)
 */
export async function updateUserStatus(userId: string, isActive: boolean): Promise<{ success: boolean; message: string }> {
  const response = await api.put(`/company-users/${userId}/status`, { is_active: isActive })
  return response.data
}

/**
 * Remove user from company (soft delete)
 */
export async function removeUserFromCompany(userId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(`/company-users/${userId}`)
  return response.data
}

/**
 * List pending invites for the current company
 */
export async function listCompanyInvites(): Promise<{ invites: CompanyInvite[]; total: number }> {
  const response = await api.get('/company-users/invites')
  return response.data
}

/**
 * Cancel a pending invite
 */
export async function cancelInvite(inviteId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(`/company-users/invites/${inviteId}`)
  return response.data
}

/**
 * Send a new invite (uses existing endpoint)
 */
export async function sendInvite(data: {
  email: string
  tenantId: string
  companyId: string
  companyType: 'shipper' | 'freight-forwarder'
  role: UserRole
}): Promise<{ success: boolean; message: string }> {
  const response = await api.post('/users/invites', data)
  return response.data
}

/**
 * Resend invite email
 */
export async function resendInvite(inviteId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.post(`/users/invites/${inviteId}/resend`)
  return response.data
}
